import { HttpClient, HttpContext, HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { ModalDialogArg } from 'app/common/model/modal-dialog-args';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { UserService } from './user.service';

/**
 * Marks a request that is already the retry after an expired-token refresh, so it is never retried again.
 * HttpContext is client-side only and is not sent to the server.
 */
export const TOKEN_REFRESH_RETRY = new HttpContextToken<boolean>(() => false);

/**
 * True if the backend rejected the request because the bearer token is expired:
 * HTTP 401 with a WWW-Authenticate header containing invalid_token.
 * Other 401s mean the user lacks permission for a function.
 */
export function isExpiredTokenError(err: any): boolean {
    if (!(err instanceof HttpErrorResponse) || err.status !== 401 || !err.headers) {
        return false;
    }
    const wwwAuthenticate: string = err.headers.get('WWW-Authenticate');
    return !!wwwAuthenticate && wwwAuthenticate.indexOf('invalid_token') !== -1;
}


@Injectable()
export class ServiceHttpInterceptor implements HttpInterceptor {
    pendingRequest = 0;
    /**
     * Variable to define which codes will display the global error
     * Some non-200 codes are used by the backend to return status
     */
    handledErrorCodes: number[] = [0, 404, 401, 409, 500];

    // Injector is used to get HttpClient and AuthService lazily, avoiding a circular dependency
    // (HttpClient -> HTTP_INTERCEPTORS -> this interceptor -> HttpClient)
    constructor(private toast: ToastService,
        private userService: UserService,
        private router: Router,
        private injector: Injector
    ) {
    }

    /**
     * The implementation of the intercept interface. In normal situations it passes the response right through
     * In the event of an error, catches the exception and raises a message
     *
     * @param req :The request object
     * @param next : The httpHandler that should then handle this response
     * @returns: An Observable Event for the handler to process
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (req.url.indexOf('keepalive.html') !== -1) {
            // Don't block on keepalive URL call
            return next.handle(req);
        }

        // URL Validation to prevent untrusted calls
        if (!this.isURLValid(req.urlWithParams)) {
            return throwError(`Call failed to invalid URL: ${req.urlWithParams}`);
        }

        this.pendingRequest++;
        this.toast.setPendingHttpCount(this.pendingRequest);

        let reqHeaders = null;
        if (this.userService.authenticatedUserEntity && (this.userService.authenticatedUserEntity.userPk != this.userService.appUserEntity.userPk)) {
            let header = new HttpHeaders().set('emulated-userid', this.userService.appUserEntity.toString());
            const existingkeys = req.headers.keys();
            if (existingkeys && existingkeys.length > 0) {
                existingkeys.forEach(eachKey => {
                    const val = req.headers.get(eachKey);
                    header = header.set(eachKey, val);
                });
            }
            reqHeaders = header;
            return this.nextHandle(req, reqHeaders, next);
        }

        reqHeaders = req.headers;
        return this.nextHandle(req, reqHeaders, next);
    }

    isURLValid(url: string): boolean {
        // https://stackoverflow.com/a/1547940 - with some modifications
        const VALID_CHARACTERS_REGEX = /[a-zA-Z0-9\s\.\/\?\~:\-\_\;\\#\[\]\@\!\$&\'\|\(\)\*\+\,\;\=%]+$/;
        // https://stackoverflow.com/a/42619368 - with some modifications
        const URL_REGEX: RegExp = /^(https?:\/\/)?(www.)?((localhost:\d+\/)|(\/?\.?[a-zA-Z0-9\s\~:\-\_\;\\#\[\]\@\!\$&\'\|\(\)\*\+\,\;\=%]+\/?\??))*$/;
        const EXCEPTIONS: string[] = ['conf.json'];
        const ALLOWED_URLS: string[] = ['mayo.edu', 'localhost:'];

        if (
            (!VALID_CHARACTERS_REGEX.test(url) // Make sure characters are valid, otherwise we could get catastrophic backtracking
                // Reject malformed percent-encoding: every '%' must be followed by two hexadecimal digits (for example, %20).
                || /%(?![0-9a-fA-F]{2})/.test(url)
                || !URL_REGEX.test(url)
                || (!ALLOWED_URLS.find(au => url.includes(au))
                    && !url.startsWith('/') // indicates it's a relative path
                )
            )
            && !EXCEPTIONS.includes(url)) {
            return false;
        }
        return true;
    }


    nextHandle(req: HttpRequest<any>, reqHeaders: HttpHeaders, next: HttpHandler): Observable<HttpEvent<any>> {
        const reqClone = req.clone({ headers: reqHeaders });
        // TODO: remove this block after emulatedUserId request header can be
        // received at the auth server
        //  if(req.url.indexOf('auth') > -1){
        //      reqClone = req.clone();
        //  }
        return next.handle(reqClone)
            .pipe(tap(event => {
                if (event instanceof HttpResponse) {
                    // do nothing for now
                    if (this.pendingRequest > 0) {
                        this.pendingRequest--;
                        this.toast.setPendingHttpCount(this.pendingRequest);
                    }
                }
            }))
            .pipe(catchError(err => {
                // if (this.pendingRequest > 0) {
                //     this.pendingRequest--;
                //     this.toast.setPendingHttpCount(this.pendingRequest);
                // }
                let countReduced = false;
                //    console.log(">> ===========================> " + (err instanceof HttpErrorResponse ));
                if (isExpiredTokenError(err)) {
                    // Expired token: not a permission problem, so don't show the modal
                    if (!req.context.get(TOKEN_REFRESH_RETRY)) {
                        return this.refreshTokenAndRetry(req, err);
                    }
                    // Already retried once: let the caller of the retry log the user out
                    this.decrementPendingCount();
                    return throwError(() => err);
                }
                if (err instanceof HttpErrorResponse) {
                    let msg = '';

                    if (err.status == 0 || err.status == 404) {
                        msg += 'Oops! Something went wrong.';
                    } else if (err.status == 500 || err.status == 409) {
                        if (err.error && err.error.message) {
                            const realMsg: string = err.error.message;
                            msg += realMsg.substring(realMsg.indexOf(':') + 1, realMsg.length);
                        } else {
                            msg += err.statusText;
                        }
                    } else if (err.status === 401) {
                        msg += 'User is not authorized to some function on this page';
                    } else if (err.status === 403) {
                        // Route to authentication error page with message
                        const reason = { id: '1', reason: err.error };
                        this.router.navigateByUrl('/not-authorized', { state: reason });
                    }

                    // We want to display errors only if certain error statuses are
                    // returned by the server. Other cases, we ignore the error
                    if (this.handledErrorCodes.includes(err.status)) {
                        //   msg +=  err.status + ': ' + err.statusText;
                        // This variable allows the message to be displayed on the modal
                        const variableData: string = 'error:' + msg;
                        const modalArg = new ModalDialogArg('modal-warn', 'GeneralException', variableData);
                        // By passing the modalArg, we ensure that the message is displayed
                        // in a modal popup instead of a toast
                        this.toast.sendMessage(msg, 'danger', null, true, modalArg);
                        // set the pending Count to 0 so the ui is not blocked
                        this.pendingRequest = 0;
                        this.toast.setPendingHttpCount(this.pendingRequest);
                        countReduced = true;
                    }
                }
                if (!countReduced) {
                    if (this.pendingRequest > 0) {
                        this.pendingRequest--;
                        this.toast.setPendingHttpCount(this.pendingRequest);
                    }
                }
                //  console.log("About to Throw Observable Exception ?")
                return throwError(err);
            }));
    }

    /**
     * Gets a new access token silently and sends the request once more through HttpClient, so the full
     * interceptor chain runs again and MsalInterceptor attaches the new token.
     * If the refresh or the retry fails with an expired token, the user is logged out instead of looping.
     * The pending count for the original request is released when the retry finishes.
     */
    refreshTokenAndRetry(req: HttpRequest<any>, originalError: HttpErrorResponse): Observable<HttpEvent<any>> {
        const authService: AuthService = this.injector.get(AuthService);
        const httpClient: HttpClient = this.injector.get(HttpClient);

        const context = new HttpContext();
        Array.from(req.context.keys()).forEach(key => context.set(key, req.context.get(key)));
        context.set(TOKEN_REFRESH_RETRY, true);
        const retryReq = req.clone({ headers: req.headers.delete('Authorization'), context });

        return authService.refreshAccessTokenSilently()
            .pipe(
                catchError(refreshErr => {
                    // InteractionRequiredAuthError: AuthService has already started a redirect login
                    if (!(refreshErr instanceof InteractionRequiredAuthError)) {
                        authService.logout();
                    }
                    return throwError(() => originalError);
                }),
                switchMap(() => httpClient.request(retryReq)
                    .pipe(catchError(retryErr => {
                        if (isExpiredTokenError(retryErr)) {
                            authService.logout();
                        }
                        return throwError(() => retryErr);
                    }))
                ),
                finalize(() => this.decrementPendingCount())
            );
    }

    private decrementPendingCount() {
        if (this.pendingRequest > 0) {
            this.pendingRequest--;
            this.toast.setPendingHttpCount(this.pendingRequest);
        }
    }
}

